import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError, api } from "@/lib/api";
import { useTranslations } from "@/hooks/use-translations";
import type { AdminUser, AssignableTeamRole, Feature, NewAdminUser } from "@/lib/access";

// Users and teams also change outside this page (a new team, someone else's
// edit), so always load fresh lists and reload them when the tab is back in view.
const fresh = { staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: "always" } as const;

/** Data for the Manager page. The API allows all of this to Super Admin only. */
export function useAdminUsers() {
	return useQuery({ queryKey: ["admin-users"], queryFn: () => api.getAdminUsers(), ...fresh });
}

export function useAdminTeams() {
	return useQuery({ queryKey: ["admin-teams"], queryFn: () => api.getAdminTeams(), ...fresh });
}

// The Manager API's errors (routes/admin.ts, admin/users.ts) by their message,
// so they can be shown in the chosen language. Anything else is shown as sent.
const ERROR_KEYS: Record<string, string> = {
	"A user with this email already exists": "emailTaken",
	"Team not found": "teamNotFound",
	"User not found": "userNotFound",
	"You can't demote or deactivate yourself": "self",
	"Already in this team": "alreadyMember",
	"Not in this team": "notMember",
	"Cannot modify owner": "owner",
	"Cannot remove owner": "owner",
	Forbidden: "forbidden",
};

/**
 * Every change refreshes the lists; your own access too, in case it was you.
 * `done` is a key in manager.toasts. Error messages come from the API.
 */
function useAdminMutation<Input>(run: (input: Input) => Promise<unknown>, done: string) {
	const queryClient = useQueryClient();
	const t = useTranslations("manager.toasts");
	const tError = useTranslations("manager.errors");
	return useMutation({
		mutationFn: run,
		onSuccess: () => {
			toast.success(t(done));
			void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
			void queryClient.invalidateQueries({ queryKey: ["admin-teams"] });
			void queryClient.invalidateQueries({ queryKey: ["my-access"] });
			void queryClient.invalidateQueries({ queryKey: ["teams"] });
		},
		onError: (error) => {
			const key = error instanceof ApiError && error.code ? ERROR_KEYS[error.code] : undefined;
			toast.error(key ? tError(key) : error instanceof Error && error.message ? error.message : t("error"));
		},
	});
}

export function useCreateAdminTeam() {
	return useAdminMutation(
		({ name, ownerId }: { name: string; ownerId: string }) => api.createAdminTeam(name, ownerId),
		"teamCreated",
	);
}

export function useCreateAdminUser() {
	return useAdminMutation(
		(input: NewAdminUser) => api.createAdminUser(input),
		"userCreated",
	);
}

export function useUpdateAdminUser() {
	return useAdminMutation(
		({ userId, changes }: { userId: string; changes: Parameters<typeof api.updateAdminUser>[1] }) =>
			api.updateAdminUser(userId, changes),
		"saved",
	);
}

type TeamPlace = { teamId: string; role: AssignableTeamRole; fullAccess: boolean; featureAccess: Feature[] };

export function useAddUserToTeam() {
	return useAdminMutation(
		({ userId, ...place }: { userId: string } & TeamPlace) => api.addAdminUserToTeam(userId, place),
		"addedToTeam",
	);
}

export function useUpdateUserTeam() {
	return useAdminMutation(
		({ userId, teamId, changes }: { userId: string; teamId: string; changes: Parameters<typeof api.updateAdminUserTeam>[2] }) =>
			api.updateAdminUserTeam(userId, teamId, changes),
		"teamUpdated",
	);
}

export function useRemoveUserFromTeam() {
	return useAdminMutation(
		({ userId, teamId }: { userId: string; teamId: string }) => api.removeAdminUserFromTeam(userId, teamId),
		"removedFromTeam",
	);
}

export type { AdminUser };
